import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Trash2, Save, Users, ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from '../../ui/button';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const FIELDS = [
  { key: 'role', label: 'Role', type: 'select', options: ['director', 'guarantor', 'applicant', 'beneficial_owner'] },
  { key: 'is_primary', label: 'Primary Applicant', type: 'checkbox' },
  { key: 'title', label: 'Title', type: 'select', options: ['Mr', 'Mrs', 'Ms', 'Miss', 'Dr', 'Other'] },
  { key: 'first_name', label: 'First Name', type: 'text', required: true },
  { key: 'middle_name', label: 'Middle Name', type: 'text' },
  { key: 'last_name', label: 'Last Name', type: 'text', required: true },
  { key: 'date_of_birth', label: 'Date of Birth', type: 'date' },
  { key: 'email', label: 'Email', type: 'email', required: true },
  { key: 'phone', label: 'Phone', type: 'text' },
  { key: 'mobile', label: 'Mobile', type: 'text' },
  { key: 'residential_address', label: 'Residential Address', type: 'text', wide: true },
  { key: 'current_landlord_mortgage_details', label: 'Landlord / Mortgage Details', type: 'text', wide: true },
  { key: 'living_same_address_as_primary', label: 'Living at same address as primary applicant', type: 'checkbox' },
  { key: 'residential_status', label: 'Residential Status', type: 'select', options: ['owner', 'renting', 'boarding', 'living_with_parents'] },
  { key: 'time_at_address', label: 'Time at Address', type: 'text', placeholder: 'e.g. 3 years' },
  { key: 'licence_card_number', label: 'Licence Card Number', type: 'text' },
  { key: 'drivers_licence_number', label: 'Licence Number', type: 'text' },
  { key: 'at_fault_accident_claim_history', label: 'At-fault accident/claim history', type: 'text', wide: true },
  { key: 'drivers_licence_state', label: 'Licence State', type: 'select', options: ['NSW','VIC','QLD','SA','WA','TAS','NT','ACT'] },
  { key: 'previous_street_address', label: 'Previous Street Address', type: 'text', wide: true },
  { key: 'previous_date_moved_in', label: 'Previous Address Date Moved In', type: 'date' },
  { key: 'previous_residential_status', label: 'Previous Address Status', type: 'select', options: ['owner', 'renting', 'boarding', 'living_with_parents'] },
  { key: 'marital_status', label: 'Marital Status', type: 'select', options: ['single', 'married', 'de_facto', 'divorced', 'widowed'] },
  { key: 'dependents', label: 'Dependents', type: 'number' },
  { key: 'employment_status', label: 'Employment', type: 'select', options: ['self_employed', 'full_time', 'part_time', 'casual', 'contractor', 'retired'] },
  { key: 'employer_name', label: 'Employer', type: 'text' },
  { key: 'occupation', label: 'Occupation', type: 'text' },
  { key: 'annual_income', label: 'Annual Income ($)', type: 'number' },
  { key: 'visa_status', label: 'Visa / Residency Status', type: 'select', options: ['citizen', 'permanent_resident', 'temporary_visa', 'student_visa', 'work_visa', 'other'] },
];

export default function ApplicantsTab({ deal, dealId, onRefresh }) {
  const applicants = deal.applicants || [];
  const [expanded, setExpanded] = useState(applicants[0]?.id || null);
  const [editing, setEditing] = useState({});
  const [saving, setSaving] = useState(null);

  const handleAdd = async () => {
    try {
      const prefill = !applicants.length ? { first_name: deal.client_name?.split(' ')[0], last_name: deal.client_name?.split(' ').slice(1).join(' '), email: deal.client_email, phone: deal.client_phone, is_primary: true, role: 'director' } : { role: 'director' };
      const res = await axios.post(`${API}/crm/deals/${dealId}/applicants`, { deal_id: dealId, ...prefill });
      toast.success('Applicant added');
      onRefresh();
      setExpanded(res.data.id);
    } catch (e) { toast.error('Failed to add applicant'); }
  };

  const handleSave = async (id) => {
    setSaving(id);
    try {
      const data = editing[id] || {};
      if (data.is_primary !== undefined) data.is_primary = Boolean(data.is_primary);
      if (data.dependents !== undefined) data.dependents = Number(data.dependents) || 0;
      if (data.annual_income !== undefined) data.annual_income = Number(data.annual_income) || 0;
      await axios.put(`${API}/crm/applicants/${id}`, data);
      toast.success('Applicant saved');
      onRefresh();
    } catch (e) { toast.error('Failed to save'); }
    finally { setSaving(null); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this applicant?')) return;
    try { await axios.delete(`${API}/crm/applicants/${id}`); toast.success('Deleted'); onRefresh(); }
    catch (e) { toast.error('Failed to delete'); }
  };

  const updateField = (id, key, value) => {
    setEditing(prev => ({ ...prev, [id]: { ...(prev[id] || {}), [key]: value } }));
  };

  return (
    <div className="max-w-3xl space-y-4" data-testid="applicants-tab">
      <div className="flex items-center justify-between">
        <h2 className="text-white font-medium flex items-center gap-2"><Users className="h-5 w-5 text-blue-400" /> Applicants / Directors</h2>
        <Button onClick={handleAdd} size="sm" className="bg-purple-600 hover:bg-purple-700 text-white" data-testid="add-applicant-btn">
          <Plus className="h-4 w-4 mr-1" />Add
        </Button>
      </div>
      {applicants.length === 0 && (
        <div className="text-center py-8 text-gray-500 text-sm">No applicants yet. Click Add to create one.</div>
      )}
      {applicants.map(app => {
        const isOpen = expanded === app.id;
        const formData = { ...app, ...(editing[app.id] || {}) };
        return (
          <div key={app.id} className="bg-navy-800/50 rounded-xl border border-white/5 overflow-hidden" data-testid={`applicant-card-${app.id}`}>
            <button onClick={() => setExpanded(isOpen ? null : app.id)}
              data-testid={`toggle-applicant-${app.id}`}
              className="w-full flex items-center justify-between px-4 py-3 hover:bg-white/5">
              <div className="flex items-center gap-2">
                <span className="text-white font-medium text-sm">{app.first_name || 'New'} {app.last_name || 'Applicant'}</span>
                {app.is_primary && <span className="text-xs bg-purple-500/20 text-purple-400 px-2 py-0.5 rounded-full">Primary</span>}
                <span className="text-xs text-gray-500 capitalize">{app.role}</span>
              </div>
              {isOpen ? <ChevronUp className="h-4 w-4 text-gray-400" /> : <ChevronDown className="h-4 w-4 text-gray-400" />}
            </button>
            {isOpen && (
              <div className="px-4 pb-4 border-t border-white/5">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
                  {FIELDS.map(f => (
                    <div key={f.key} className={f.wide ? 'md:col-span-2' : ''}>
                      {f.type === 'checkbox' ? (
                        <label className="flex items-center gap-2 text-sm text-gray-300 cursor-pointer">
                          <input type="checkbox" checked={!!formData[f.key]} onChange={e => updateField(app.id, f.key, e.target.checked)}
                            data-testid={`applicant-${app.id}-${f.key}`}
                            className="rounded border-white/20 bg-navy-800 text-purple-500" />
                          {f.label}
                        </label>
                      ) : (
                        <>
                          <label className="block text-xs text-gray-400 mb-1">{f.label}{f.required && <span className="text-red-400">*</span>}</label>
                          {f.type === 'select' ? (
                            <select value={formData[f.key] || ''} onChange={e => updateField(app.id, f.key, e.target.value)}
                              data-testid={`applicant-${app.id}-${f.key}`}
                              className="w-full bg-navy-800 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:border-purple-500 focus:outline-none">
                              <option value="">Select...</option>
                              {f.options.map(o => <option key={o} value={o}>{o}</option>)}
                            </select>
                          ) : (
                            <input type={f.type} value={formData[f.key] ?? ''} placeholder={f.placeholder || ''}
                              onChange={e => updateField(app.id, f.key, e.target.value)}
                              data-testid={`applicant-${app.id}-${f.key}`}
                              className="w-full bg-navy-800 border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-600 focus:border-purple-500 focus:outline-none" />
                          )}
                        </>
                      )}
                    </div>
                  ))}
                </div>
                <div className="flex justify-between mt-4">
                  <Button onClick={() => handleDelete(app.id)} variant="ghost" size="sm" className="text-red-400 hover:text-red-300 hover:bg-red-500/10" data-testid={`delete-applicant-${app.id}`}>
                    <Trash2 className="h-4 w-4 mr-1" />Delete
                  </Button>
                  <Button onClick={() => handleSave(app.id)} disabled={saving === app.id} size="sm" className="bg-purple-600 hover:bg-purple-700 text-white" data-testid={`save-applicant-${app.id}`}>
                    <Save className="h-4 w-4 mr-1" />{saving === app.id ? 'Saving...' : 'Save'}
                  </Button>
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
